import { gql } from "@outschool/ui-apollo";

import { ParticipantFragment } from "./MessageFragment";

const teacherMessagingMessageThreadFragment = gql`
  fragment TeacherMessagingMessageThreadFragment on IMessageThread {
    uid
    isRead
    matchedSearchResults {
      threadUid
      highlightedMatch
    }
    activity {
      uid
      title
      titleTranslated
      formatString
      isSelfPaced
      photo {
        fileUrl
      }
    }
    section {
      uid
      currentSectionLesson {
        lesson {
          uid
          title
          lessonNumber
        }
      }
    }
    ... on MessageThread {
      section {
        enrollments(status: Enrolled) {
          uid
          parent {
            uid
          }
          learner {
            uid
            name
          }
        }
      }
      participants {
        ...ParticipantFragment
        activitiesWithCurrentUser {
          uid
        }
        parent {
          uid
          photo
          publicName
        }
        avatar
        avatarUrl
      }
      adultMessages: messages {
        uid
        sender {
          uid
          name
          photo
          timeZone
          headline
          leader_link
        }
        participants {
          ...ParticipantFragment
          isLearner
          avatar
          avatarUrl
        }
        activity_uid
        section_uid
        thread_uid
        subject
        content
        created_at
      }
    }
    ... on PrivateClassThread {
      canBeMarkedUnread
      section {
        enrollments(status: Enrolled) {
          uid
          parent {
            uid
          }
          learner {
            uid
            name
          }
        }
      }
      privateMessages: messages {
        uid
        sender {
          uid
          name
          photo
          avatar
          avatarUrl
          isLearner
        }
        activity_uid
        section_uid
        thread_uid
        subject
        content
        sentAt
      }
      enrollment {
        learner {
          uid
          name
          avatar
          avatarUrl
        }
        parent {
          uid
          photo
          publicName
        }
      }
    }
    ... on ClassPostCommentThread {
      canBeMarkedUnread
      isPublic
      latestMessage {
        uid
        sender {
          uid
          name
          photo
          avatar
          avatarUrl
          isLearner
        }
        activity_uid
        section_uid
        thread_uid
        subject
        content
        sendAt
      }
      classPost {
        uid
        title
        blockType
        lesson {
          uid
          title
          lessonNumber
        }
      }
    }
  }
  ${ParticipantFragment}
`;

export const teacherMessagingConversationsQuery = gql`
  query TeacherMessagingConversations(
    $offset: Int!
    $limit: Int!
    $filters: ConversationFiltersInput
    $audience: Audience
    $messageSelection: MessageSelection
    $sortOrder: SortOrder
    $viewAsOrgTeacherUid: String
  ) {
    currentUser {
      uid
      unreadMessageThreadGroupsCount
      unreadPrivateClassMessageThreadsCount
      unreadAssignmentCommentThreadsCount
      conversations(
        offset: $offset
        limit: $limit
        filters: $filters
        audience: $audience
        messageSelection: $messageSelection
        sortOrder: $sortOrder
        viewAsOrgTeacherUid: $viewAsOrgTeacherUid
      )
        @connection(
          key: "teacherMessagingConversations"
          filter: [
            "audience"
            "filters"
            "messageSelection"
            "sortOrder"
            "viewAsOrgTeacherUid"
          ]
        ) {
        ...TeacherMessagingMessageThreadFragment
      }
      conversationsCount(
        filters: $filters
        audience: $audience
        messageSelection: $messageSelection
        viewAsOrgTeacherUid: $viewAsOrgTeacherUid
      )
    }
  }
  ${teacherMessagingMessageThreadFragment}
`;

export const teacherPublicClassPostCommentUnreadCountQuery = gql`
  query TeacherPublicClassPostCommentUnreadCount {
    currentUser {
      uid
      unreadPublicClassPostCommentThreadsCount
    }
  }
`;

export const teacherLearnerConversationActivitiesQuery = gql`
  query TeacherLearnerConversationActivities($viewAsOrgTeacherUid: String) {
    currentUser {
      uid
      teacherLearnerConversationActivities(
        viewAsOrgTeacherUid: $viewAsOrgTeacherUid
      ) {
        uid
        title
      }
    }
  }
`;

export const teacherLearnerConversationLearnersQuery = gql`
  query TeacherLearnerConversationLearners(
    $activityUid: ID!
    $viewAsOrgTeacherUid: String
  ) {
    currentUser {
      uid
      teacherLearnerConversationLearners(
        activityUid: $activityUid
        viewAsOrgTeacherUid: $viewAsOrgTeacherUid
      ) {
        uid
        name
      }
    }
  }
`;

export const searchConversationFiltersQuery = gql`
  query SearchConversationFilters(
    $searchQuery: String!
    $audience: Audience!
    $existingFilters: ConversationFiltersInput
    $viewAsOrgTeacherUid: String
  ) {
    searchConversationFilters(
      searchQuery: $searchQuery
      audience: $audience
      existingFilters: $existingFilters
      viewAsOrgTeacherUid: $viewAsOrgTeacherUid
    ) {
      uid
      type
      name
      highlight
      resultCount
      threadUid
      parentUid
    }
  }
`;

export const searchTeacherEnrollmentRecipientsQuery = gql`
  query SearchTeacherEnrollmentRecipients(
    $searchQuery: String!
    $audience: Audience!
  ) {
    searchTeacherEnrollmentRecipients(
      searchQuery: $searchQuery
      audience: $audience
    ) {
      uid
      name
      audience
      parentName
      learnerNames
      photo
      avatar
      avatarUrl
      isLearner
    }
  }
`;

export const composeRecipientParentClassesQuery = gql`
  query ComposeRecipientParentClasses($parentUid: ID!, $teacherUid: ID!) {
    parent(uid: $parentUid) {
      uid
      enrollmentsWithTeacher(
        teacherUid: $teacherUid
        excludeDeletedActivities: true
      ) {
        uid
        section {
          uid
          activity {
            uid
            title
          }
        }
      }
    }
    waitlistSeatsForParentAndTeacher(
      parentUid: $parentUid
      teacherUid: $teacherUid
    ) {
      uid
      section {
        uid
        activity {
          uid
          title
        }
      }
    }
  }
`;

export const composeRecipientLearnerClassesQuery = gql`
  query ComposeRecipientLearnerClasses(
    $participantLearnerUids: [ID!]!
    $learnerOrEnrollmentUid: String!
  ) {
    participants(uids: $participantLearnerUids, audience: learners) {
      uid
      sectionsWithCurrentUser {
        uid
        usersCanSendPrivateMessage
        selectedLearnerEnrollment: selectedEnrollment(
          learnerOrEnrollmentUid: $learnerOrEnrollmentUid
        ) {
          __typename
          ... on Enrollment {
            uid
            usersCanCommunicate
          }
        }
        activity {
          uid
          title
        }
      }
    }
  }
`;
